import * as THREE from 'three';

/**
 * Manages the Three.js renderer
 */
export class RendererService {
  private renderer: THREE.WebGLRenderer;
  private pixelRatio: number;
  private defaultClearColor: THREE.Color;
  private defaultClearAlpha: number;

  /**
   * Creates a new RendererService instance
   * @param renderer The Three.js renderer to manage
   */
  constructor(renderer: THREE.WebGLRenderer) {
    this.renderer = renderer;
    this.pixelRatio = Math.min(window.devicePixelRatio, 2);
    
    // Set default clear color first
    const defaultColor = new THREE.Color(0x000000);
    this.defaultClearColor = defaultColor;
    this.defaultClearAlpha = 1.0;
    
    // Set the clear color on the renderer
    this.renderer.setClearColor(this.defaultClearColor, this.defaultClearAlpha);

    // Configure renderer
    this.setupRenderer();
  }

  /**
   * Sets up the renderer with default settings
   */
  private setupRenderer(): void {
    // Set pixel ratio
    this.renderer.setPixelRatio(this.pixelRatio);

    // Enable shadow mapping
    this.renderer.shadowMap.enabled = true;
    this.renderer.shadowMap.type = THREE.PCFSoftShadowMap;

    // Set tone mapping
    this.renderer.toneMapping = THREE.ACESFilmicToneMapping;
    this.renderer.toneMappingExposure = 1.0;

    // Enable color space
    this.renderer.outputColorSpace = THREE.SRGBColorSpace;
  }

  /**
   * Resizes the renderer
   * @param width The new width
   * @param height The new height
   */
  public resize(width: number, height: number): void {
    this.renderer.setSize(width, height);
  }

  /**
   * Sets the pixel ratio
   * @param ratio The new pixel ratio
   */
  public setPixelRatio(ratio: number): void {
    this.pixelRatio = Math.max(1, Math.min(ratio, 2));
    this.renderer.setPixelRatio(this.pixelRatio);
  }

  /**
   * Sets the clear color
   * @param color The new clear color
   * @param alpha The new clear alpha
   */
  public setClearColor(color: THREE.ColorRepresentation, alpha: number = 1): void {
    this.renderer.setClearColor(color, alpha);
  }

  /**
   * Resets the clear color to the default
   */
  public resetClearColor(): void {
    this.renderer.setClearColor(this.defaultClearColor, this.defaultClearAlpha);
  }

  /**
   * Enables or disables shadow mapping
   * @param enabled Whether shadow mapping should be enabled
   */
  public setShadowsEnabled(enabled: boolean): void {
    this.renderer.shadowMap.enabled = enabled;
  }

  /**
   * Sets the shadow map type
   * @param type The shadow map type
   */
  public setShadowMapType(type: THREE.ShadowMapType): void {
    this.renderer.shadowMap.type = type;
  }

  /**
   * Sets the tone mapping
   * @param toneMapping The tone mapping type
   * @param exposure The tone mapping exposure
   */
  public setToneMapping(toneMapping: THREE.ToneMapping, exposure: number = 1.0): void {
    this.renderer.toneMapping = toneMapping;
    this.renderer.toneMappingExposure = exposure;
  }

  /**
   * Gets the renderer
   * @returns The Three.js renderer
   */
  public getRenderer(): THREE.WebGLRenderer {
    return this.renderer;
  }

  /**
   * Cleans up resources when the service is no longer needed
   */
  public dispose(): void {
    // Dispose of any renderer-specific resources
    this.renderer.dispose();
  }
} 